import React, { useEffect, useState } from 'react'
import {
  ArrowCounterClockwise,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react'
import { Avatar } from 'antd'
import { getNameSignature } from 'helper/utility'
import { useGetFamilyMembers } from 'api/family/family'
import { failureMessage } from 'api/global'
import { CheckINOutAttendee } from 'types/event/eventAttendance'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import Spinner from 'components/Spinner'
import { Person } from 'types/globalTypes'
import { useCalcluateDropInPrice } from 'api/Event/Attendance'
import ButtonSpinner from 'components/global/ButtonSpinner'

interface ParticipantAndDetailsProps {
  type?: 'check-in' | 'check-out' | 'drop-out' | 'drop-in'
}

const ParticipantAndDetails: React.FC<ParticipantAndDetailsProps> = ({
  type,
}) => {
  const [showItem, setShowItem] = useState(0)
  const [showReload, setShowReload] = useState(false)
  const { participantData }: { participantData: CheckINOutAttendee | null } =
    eventAttendanceStore(store => ({
      participantData: store.participantData,
    }))

  const {
    data: familyMembers,
    isLoading: familyMembersLoading,
    isFetching: familyMembersFetching,
    refetch: familyMembersRefetch,
  } = useGetFamilyMembers(participantData.familyId)

  const {
    data: dropCalculatedPrice,
    isLoading: dropCalculatedPriceLoading,
    refetch: dropCalculatedPriceRefetch,
    isFetching: dropCalculatedPriceFetching,
  } = useCalcluateDropInPrice(participantData.checkInId)

  useEffect(() => {
    if (participantData?.familyId) {
      familyMembersRefetch()
    } else {
      failureMessage("Participant doesn't have familyId")
    }
  }, [participantData.familyId])

  useEffect(() => {
    if (type === 'drop-out') {
      dropCalculatedPriceRefetch()
      setShowReload(false)
      const timer = setTimeout(() => {
        setShowReload(true)
      }, 60000)
      return () => clearTimeout(timer)
    }
  }, [participantData])

  return (
    <>
      <div className="bg-white radius-8 px-15px py-15px w-350px">
        {familyMembersLoading || familyMembersFetching ? (
          <div className="py-40px flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <h2 className="text-16 w-600">Participant details</h2>
            <div className="flex-center mt-10px">
              <Avatar size={40} src={participantData.photo?.post}>
                {getNameSignature(
                  `${participantData.firstName} ${participantData.lastName}`,
                )}
              </Avatar>
              <h3 className="text-14px w-500 ml-10px">{`${participantData.firstName} ${participantData.lastName}`}</h3>
            </div>

            <div
              className="flex align-center gap-20px mt-10px pointer"
              onClick={() => setShowItem(showItem === 1 ? 0 : 1)}
            >
              <h3 className="prim-txt text-underline w-500 text-16 pointer">
                Show Guardians
              </h3>
              <div>
                {showItem === 1 ? (
                  <CaretUp size={20} color="#2BA7CA" />
                ) : (
                  <CaretDown size={20} color="#2BA7CA" />
                )}
              </div>
            </div>

            {showItem === 1 && (
              <>
                {familyMembers.guardians.length ? (
                  familyMembers.guardians.map((guardian: Person) => {
                    return (
                      <div className="flex-center mt-10px" key={guardian._id}>
                        <Avatar size={40} src={guardian.photo?.post}>
                          {getNameSignature(
                            `${guardian.firstName} ${guardian.lastName}`,
                          )}
                        </Avatar>
                        <h3 className="text-14px w-500 ml-10px">
                          {`${guardian.firstName} ${guardian.lastName}`}
                        </h3>
                      </div>
                    )
                  })
                ) : (
                  <p>No guardians found for this family</p>
                )}
              </>
            )}

            <div
              className="flex align-center gap-20px mt-10px pointer"
              onClick={() => setShowItem(showItem === 2 ? 0 : 2)}
            >
              <h3 className="prim-txt text-underline w-500 text-16 pointer">
                Show Authorized adults
              </h3>
              <div>
                {showItem === 2 ? (
                  <CaretUp size={20} color="#2BA7CA" />
                ) : (
                  <CaretDown size={20} color="#2BA7CA" />
                )}
              </div>
            </div>

            {showItem === 2 && (
              <>
                {familyMembers.adults.length ? (
                  familyMembers.adults.map((adult: Person) => {
                    return (
                      <div className="flex-center mt-10px" key={adult._id}>
                        <Avatar size={40} src={adult.photo?.post}>
                          {getNameSignature(
                            `${adult.firstName} ${adult.lastName}`,
                          )}
                        </Avatar>
                        <h3 className="text-14px w-500 ml-10px">
                          {`${adult.firstName} ${adult.lastName}`}
                        </h3>
                      </div>
                    )
                  })
                ) : (
                  <p>No adults found for this family</p>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="bg-white radius-8 p-15px mt-20px w-350px">
        <h2 className="text-16 w-600">Session details</h2>
        <div className="flex justify-between align-center">
          <p>Name</p>
          <div className="flex gap-10px align-center">
            <h3>{participantData.ticket.name}</h3>
          </div>
        </div>
        {/* <div className="flex justify-between align-center">
          <p>Hours Scheduled</p>
          <h3>3hrs</h3>
        </div> */}
        <div className="flex justify-between align-center">
          <h3 className={`${type === 'drop-out' ? 'w-400 text-14' : ''}`}>
            Price
          </h3>
          <h3>${participantData.ticket.price}</h3>
        </div>

        {type === 'drop-out' && (
          <>
            <div className="flex justify-between align-center">
              <h3>Total</h3>
              <div className="flex gap-10px align-center">
                {dropCalculatedPriceLoading || dropCalculatedPriceFetching ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  <h3>${dropCalculatedPrice.calculatedAmount}</h3>
                )}

                {showReload && (
                  <button
                    style={{
                      width: '30px',
                      height: '30px',
                      background: '#2ba7ca',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: 'none',
                    }}
                    onClick={() => {
                      setShowReload(false)
                      dropCalculatedPriceRefetch()
                      setTimeout(() => setShowReload(true), 60000)
                    }}
                  >
                    <ArrowCounterClockwise size={20} color="#fff" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default ParticipantAndDetails
